import { useMemo, useState, useEffect, useCallback } from 'react';
import ResizableTableHeaderCell from '../components/ResizableTableHeaderCell';

const LS_PREFIX = 'policylens:colwidths:';

function loadStored(storageKey) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveStored(storageKey, overrides) {
    try {
        localStorage.setItem(LS_PREFIX + storageKey, JSON.stringify(overrides));
    } catch {
        /* ignore quota / private mode */
    }
}

/**
 * Persists column widths to localStorage and merges into Ant Design Table columns.
 *
 * @param {string} storageKey - Unique key per table (e.g. 'question-bank').
 * @param {import('antd').TableProps['columns']} baseColumns - Columns with `key` and `width` for resizable cols.
 * @returns {{ columns: typeof baseColumns, components: object }}
 */
export function useResizableTableColumns(storageKey, baseColumns) {
    const [overrides, setOverrides] = useState(() => loadStored(storageKey));

    useEffect(() => {
        setOverrides(loadStored(storageKey));
    }, [storageKey]);

    const setColumnWidth = useCallback((colKey, w) => {
        setOverrides((prev) => ({ ...prev, [colKey]: w }));
    }, []);

    useEffect(() => {
        const t = setTimeout(() => saveStored(storageKey, overrides), 200);
        return () => clearTimeout(t);
    }, [storageKey, overrides]);

    const columns = useMemo(() => {
        if (!baseColumns?.length) return baseColumns;

        return baseColumns.map((col) => {
            if (col.key == null) {
                return col;
            }
            const defW = col.width != null ? col.width : 120;
            const w = overrides[col.key] ?? defW;
            const minWidth = col.minWidth ?? 48;

            return {
                ...col,
                width: w,
                onHeaderCell: () => ({
                    width: w,
                    minWidth,
                    onResize: (newWidth) => setColumnWidth(col.key, newWidth),
                }),
            };
        });
    }, [baseColumns, overrides, setColumnWidth]);

    const components = useMemo(
        () => ({
            header: {
                cell: ResizableTableHeaderCell,
            },
        }),
        []
    );

    return { columns, components };
}
